import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axios';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';
import config from '../../config';
import './Company.css';
import { toast } from '../../components/Toast';

function BranchManagement() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phoneNumber: '91' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/company/branches');
      const data = res.data.data || res.data;
      setBranches(Array.isArray(data) ? data : data.content || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBranches(); }, [fetchBranches]);

  const showSuccess = (msg) => { toast.success(msg); };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', address: '', phoneNumber: '91' });
    setLogoFile(null);
    setLogoPreview(null);
    setShowForm(true);
  };

  const openEdit = (branch) => {
    setEditing(branch);
    setForm({ name: branch.name, address: branch.address || '', phoneNumber: branch.phoneNumber || '91' });
    setLogoFile(null);
    setLogoPreview(branch.logoUrl ? `${config.apiBaseUrl}${branch.logoUrl}` : null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let savedBranch;
      if (editing) {
        const res = await axios.put(`/company/branches/${editing.id}`, form);
        savedBranch = res.data.data || res.data;
      } else {
        const res = await axios.post('/company/branches', form);
        savedBranch = res.data.data || res.data;
      }
      // Upload logo if selected — do NOT set Content-Type manually;
      // the browser must set it with the correct multipart boundary
      let logoError = null;
      if (logoFile && savedBranch?.id) {
        const fd = new FormData();
        fd.append('file', logoFile);
        try {
          await axios.post(`/company/branches/${savedBranch.id}/logo`, fd);
          showSuccess(editing ? 'Branch updated with logo' : 'Branch created with logo');
        } catch (logoErr) {
          showSuccess(editing ? 'Branch updated' : 'Branch created');
          logoError = logoErr.response?.data?.message || 'Branch saved but logo upload failed';
        }
      } else {
        showSuccess(editing ? 'Branch updated' : 'Branch created');
      }
      closeForm();
      fetchBranches();
      if (logoError) toast.error(logoError);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'BRANCH_LIMIT_EXCEEDED') {
        window.dispatchEvent(new CustomEvent('plan-limit-exceeded', {
          detail: { code, message: err.response.data.message }
        }));
      } else {
        toast.error(err.response?.data?.message || 'Failed to save branch');
      }
    }
  };

  const handleDelete = (branch) => {
    setBranchToDelete(branch);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!branchToDelete) return;
    try {
      await axios.delete(`/company/branches/${branchToDelete.id}`);
      setShowConfirmModal(false);
      setBranchToDelete(null);
      showSuccess('Branch deleted');
      fetchBranches();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete branch');
      setShowConfirmModal(false);
      setBranchToDelete(null);
    }
  };

  return (
    <div className="co-page">
      <div className="co-header">
        <h1 className="co-title">Branch Management</h1>
        <button className="co-btn co-btn-primary" onClick={openCreate}>+ New Branch</button>
      </div>

      {showForm && (
        <div className="co-modal-overlay">
          <div className="co-modal">
            <h2>{editing ? 'Edit Branch' : 'Create Branch'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Branch Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea
                  rows={3}
                  value={form.address}
                  onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={form.phoneNumber}
                  onChange={e => setForm(p => ({ ...p, phoneNumber: e.target.value.replace(/[a-zA-Z]/g, '') }))}
                />
              </div>

              {/* Logo upload */}
              <div className="form-group">
                <label>Branch Logo</label>
                {logoPreview && (
                  <div style={{ marginBottom: 8 }}>
                    <img src={logoPreview} alt="logo preview"
                      style={{ width: 80, height: 80, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 6, padding: 4 }} />
                  </div>
                )}
                <input type="file" accept="image/jpeg,image/png,image/webp"
                  onChange={handleLogoChange} />
                <small style={{ color: '#888', fontSize: 12 }}>JPG, PNG or WebP — max 2 MB</small>
              </div>
              <div className="co-modal-actions">
                <button type="button" className="co-btn" onClick={closeForm}>Cancel</button>
                <button type="submit" className="co-btn co-btn-primary">
                  {editing ? 'Save Changes' : 'Create Branch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <div className="co-loading">Loading branches...</div> : (
        <div className="co-cards">
          {branches.length === 0 ? (
            <div className="co-empty">No branches yet. Create your first branch.</div>
          ) : branches.map(b => (
            <div key={b.id} className="co-card">
              <div className="co-card-body">
                {b.logoUrl && (
                  <img src={`${config.apiBaseUrl}${b.logoUrl}`} alt={b.name}
                    style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 6, border: '1px solid #eee', marginBottom: 8, display: 'block' }} />
                )}
                <h3>{b.name}</h3>
                {b.address && <p className="co-card-sub">{b.address}</p>}
                {b.phoneNumber && <p className="co-card-sub">{b.phoneNumber}</p>}
                <p className="co-card-meta">{b.userCount ?? 0} users</p>
              </div>
              <div className="co-card-actions">
                <button className="co-btn co-btn-sm" onClick={() => openEdit(b)}>Edit</button>
                <button className="co-btn co-btn-sm co-btn-danger" onClick={() => handleDelete(b)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <DeleteConfirmModal
        isOpen={showConfirmModal}
        title="Delete Branch"
        message={<>Are you sure you want to delete branch <strong>{branchToDelete?.name}</strong>?</>}
        onCancel={() => { setShowConfirmModal(false); setBranchToDelete(null); }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

export default BranchManagement;
